<app-report-shell
  [user]="user"
  [currentTime]="''"
  [sidebarCollapsed]="sidebarCollapsed"
  [mobileSidebarOpen]="mobileSidebarOpen"
  (menuToggle)="handleMenuToggle()"
  (closeMobileSidebar)="closeMobileSidebar()"
>
  <main class="daily-generation-page">
    <section class="daily-generation-page__panel app-card">
      <div class="daily-generation-page__panel-header">
        <div class="daily-generation-page__heading-copy">
          <h2 class="app-title-serif">Daily Generation Comparison</h2>
        </div>
        <button
          type="button"
          class="app-button app-button--primary daily-generation-page__export-button"
          [disabled]="isLoading || isExporting || !selectedSources.length"
          (click)="handleExportToExcel()"
        >
          {{ isExporting ? 'Exporting...' : 'Export Excel' }}
        </button>
      </div>

      <div class="daily-generation-page__panel-body">
        <div class="daily-generation-page__filters-row">
          <form class="daily-generation-filters" [formGroup]="filtersForm">
            <label class="app-form-field daily-generation-filters__field">
              <span class="app-form-label">Frequency</span>
              <select class="app-form-input daily-generation-filters__input" formControlName="data_frequency">
                @for (option of frequencyOptions; track option.value) {
                  <option [value]="option.value">{{ option.label }}</option>
                }
              </select>
            </label>

            @if (isDailyFrequency()) {
              <label class="app-form-field daily-generation-filters__field">
                <span class="app-form-label">From Date</span>
                <input
                  class="app-form-input daily-generation-filters__input"
                  [type]="getDateInputType()"
                  [readOnly]="isBsDateMode"
                  formControlName="from_date"
                  (click)="openBsDatePicker('from_date')"
                />
              </label>
              <label class="app-form-field daily-generation-filters__field">
                <span class="app-form-label">To Date</span>
                <input
                  class="app-form-input daily-generation-filters__input"
                  [type]="getDateInputType()"
                  [readOnly]="isBsDateMode"
                  formControlName="to_date"
                  (click)="openBsDatePicker('to_date')"
                />
              </label>
            } @else {
              <label class="app-form-field daily-generation-filters__field">
                <span class="app-form-label">Date</span>
                <input
                  class="app-form-input daily-generation-filters__input"
                  [type]="getDateInputType()"
                  [readOnly]="isBsDateMode"
                  formControlName="date"
                  (click)="openBsDatePicker('date')"
                />
              </label>
            }

            @if (bsCalendarOpen) {
              <dialog class="daily-generation-bs-calendar" open aria-label="Nepali calendar date picker" appClickOutside (appClickOutside)="closeBsDatePicker()">
                <div class="daily-generation-bs-calendar__header">
                  <button type="button" class="daily-generation-bs-calendar__nav" aria-label="Previous month" (click)="shiftBsCalendarMonth(-1)">‹</button>
                  <div class="daily-generation-bs-calendar__title">
                    {{ bsCalendarMonth?.bs_month_name || 'Nepali Calendar' }} {{ bsCalendarMonth?.bs_year || '' }}
                  </div>
                  <button type="button" class="daily-generation-bs-calendar__nav" aria-label="Next month" (click)="shiftBsCalendarMonth(1)">›</button>
                </div>
                <div class="daily-generation-bs-calendar__weekdays">
                  @for (weekday of bsCalendarWeekdays; track weekday) {
                    <span>{{ weekday }}</span>
                  }
                </div>
                @if (bsCalendarLoading) {
                  <div class="daily-generation-bs-calendar__loading">Loading calendar...</div>
                } @else {
                  <div class="daily-generation-bs-calendar__grid">
                    @for (cell of (bsCalendarMonth?.cells || []); track cell.bs_date) {
                      <button
                        type="button"
                        class="daily-generation-bs-calendar__day"
                        [class.daily-generation-bs-calendar__day--muted]="!cell.in_current_month"
                        [class.daily-generation-bs-calendar__day--selected]="cell.is_selected"
                        [class.daily-generation-bs-calendar__day--today]="cell.is_today"
                        (click)="selectBsCalendarCell(cell)"
                      >
                        {{ cell.bs_date_label }}
                      </button>
                    }
                  </div>
                }
              </dialog>
            }

            <label class="app-form-field daily-generation-filters__field">
              <span class="app-form-label">Show</span>
              <select class="app-form-input daily-generation-filters__input" formControlName="target_type">
                @for (option of targetTypeOptions; track option.value) {
                  <option [value]="option.value">{{ option.label }}</option>
                }
              </select>
            </label>

            <label class="app-form-field daily-generation-filters__field daily-generation-filters__field--wide">
              <span class="app-form-label">{{ getTargetFilterLabel() }}</span>
              <details #targetFilter class="daily-generation-filters__multi-select">
                <summary class="daily-generation-filters__multi-select-summary">{{ getSelectedTargetLabel() }}</summary>
                <div class="daily-generation-filters__multi-select-menu" (scroll)="handleTargetOptionsScroll($event)">
                  <input
                    class="app-form-input daily-generation-filters__search-input"
                    type="search"
                    [(ngModel)]="targetSearchTerm"
                    [ngModelOptions]="{ standalone: true }"
                    placeholder="Search"
                  />
                  @for (option of getFilteredTargetOptions(); track trackByOption($index, option)) {
                    <label class="daily-generation-filters__multi-select-option">
                      <input type="checkbox" [checked]="isTargetSelected(option.id)" (change)="toggleTargetSelection(option.id, $event)" />
                      <span>{{ option.name }}</span>
                    </label>
                  }
                </div>
              </details>
            </label>

            <div class="app-form-field daily-generation-filters__field daily-generation-filters__field--sources">
              <span class="app-form-label">Data Source</span>
              <details class="daily-generation-filters__multi-select">
                <summary class="daily-generation-filters__multi-select-summary">{{ getSelectedSourceLabel() }}</summary>
                <div class="daily-generation-filters__multi-select-menu">
                @for (source of sourceOptions; track source.value) {
                  <label class="daily-generation-filters__multi-select-option">
                    <input
                      type="checkbox"
                      [checked]="isSourceSelected(source.value)"
                      (change)="toggleSourceSelection(source.value, $event)"
                    />
                    <span>{{ source.label }}</span>
                  </label>
                }
                </div>
              </details>
            </div>

            <div class="daily-generation-filters__actions">
              <button type="button" class="app-button app-button--secondary app-filter-actions__button" (click)="clearFilters()">
                Clear
              </button>
            </div>
          </form>

        </div>

        @if (errorMessage) {
          <div class="daily-generation-page__error">{{ errorMessage }}</div>
        }

        <div class="daily-generation-page__table-wrap app-table-scroll app-scrollbar-x" (scroll)="handleEntityTableScroll($event)">
          <table class="daily-generation-table">
            <thead>
              <tr>
                <th class="daily-generation-table__sticky daily-generation-table__sticky--index" scope="col" rowspan="3">Sr. No.</th>
                <th class="daily-generation-table__sticky daily-generation-table__sticky--time" scope="col" rowspan="3">
                  {{ isDailyFrequency() ? 'Date' : 'Time Blocks' }}
                </th>
                @if (entities.length) {
                  @for (entity of entities; track trackByEntity($index, entity)) {
                    <th class="daily-generation-table__entity-header" scope="colgroup" [attr.colspan]="selectedSources.length + 1">
                      {{ entity.name }}
                    </th>
                  }
                } @else {
                  <th class="daily-generation-table__entity-header" scope="col">Generator/Substation</th>
                }
              </tr>
              <tr>
                @if (entities.length) {
                  @for (entity of entities; track trackByEntity($index, entity)) {
                    <th class="daily-generation-table__metric-header" scope="col" rowspan="2">Power(MW)</th>
                    <th class="daily-generation-table__metric-header" scope="colgroup" [attr.colspan]="selectedSources.length">Sources</th>
                  }
                } @else {
                  <th class="daily-generation-table__metric-header" scope="col">Power(MW)</th>
                }
              </tr>
              <tr>
                @if (entities.length) {
                  @for (entity of entities; track trackByEntity($index, entity)) {
                    @for (source of selectedSources; track trackBySource($index, source)) {
                      <th class="daily-generation-table__source-header" scope="col">{{ getSourceLabel(source) }}</th>
                    }
                  }
                } @else {
                  <th class="daily-generation-table__source-header" scope="col">Source</th>
                }
              </tr>
            </thead>
            <tbody>
              @if (isLoading) {
                <tr>
                  <td [attr.colspan]="getTableColspan()" class="daily-generation-page__empty-state">Loading daily generation comparison...</td>
                </tr>
              } @else if (rows.length && entities.length) {
                @for (row of rows; track trackByRow($index, row)) {
                  <tr class="daily-generation-table__body-row">
                    <td class="daily-generation-table__sticky daily-generation-table__sticky--index daily-generation-table__index-cell">
                      {{ row.serial_no }}
                    </td>
                    <th class="daily-generation-table__sticky daily-generation-table__sticky--time daily-generation-table__time-cell" scope="row">
                      {{ row.date_time_label }}
                    </th>
                    @for (entity of entities; track trackByEntity($index, entity)) {
                      <td [class]="'daily-generation-table__cell daily-generation-table__cell--power ' + getPowerCellClass(row, entity)">
                        {{ getPowerValue(row, entity) }}
                      </td>
                      @for (source of selectedSources; track trackBySource($index, source)) {
                        <td [class]="'daily-generation-table__cell ' + getValueCellClass(row, entity, source)">{{ getCellValue(row, entity, source) }}</td>
                      }
                    }
                  </tr>
                }
              } @else {
                <tr>
                  <td [attr.colspan]="getTableColspan()" class="daily-generation-page__empty-state">
                    No comparison data found for the selected filters.
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        <div class="daily-generation-page__legend" aria-label="Power column legend">
          <span class="daily-generation-page__legend-item">
            <span class="daily-generation-page__legend-swatch daily-generation-page__legend-swatch--generator"></span>
            Generator/Substation Declared value
          </span>
          <span class="daily-generation-page__legend-item">
            <span class="daily-generation-page__legend-swatch daily-generation-page__legend-swatch--admin"></span>
            Admin Declared value
          </span>
        </div>
      </div>
    </section>
  </main>
</app-report-shell>
